/**
 * Campaign Service - API client for marketing campaign endpoints
 */

import api from './api'
import { createLogger } from '../utils/logger'

const logger = createLogger('campaignService')

// ============================================================================
// VISITOR ID TRACKING
// ============================================================================

const VISITOR_ID_KEY = '_rptr_vid'

/**
 * Get or create a visitor ID for anonymous tracking
 * @returns {string} UUID v4 visitor ID
 */
export const getVisitorId = () => {
  let visitorId = localStorage.getItem(VISITOR_ID_KEY)
  if (!visitorId) {
    visitorId = crypto.randomUUID()
    localStorage.setItem(VISITOR_ID_KEY, visitorId)
  }
  return visitorId
}

// ============================================================================
// PUBLIC ENDPOINTS
// ============================================================================

/**
 * Resolve a tracking code (referral or campaign)
 * @param {string} code - The tracking code
 * @returns {Promise<Object>} Resolution result
 */
export const resolveTrackingCode = async (code) => {
  try {
    const response = await api.get(`/campaigns/resolve/${encodeURIComponent(code)}`)
    return response.data
  } catch (error) {
    logger.error('Error resolving tracking code:', error)
    return {
      success: false,
      data: {
        type: 'none',
        valid: false,
        error: error.response?.data?.error?.message || 'Failed to resolve tracking code'
      }
    }
  }
}

/**
 * Record a campaign hit (fire-and-forget)
 * @param {Object} data - Hit data
 */
export const recordCampaignHit = async (data) => {
  try {
    const visitorId = getVisitorId()
    await api.post('/campaigns/hit', {
      campaignCode: data.campaignCode,
      visitorId,
      sessionId: data.sessionId,
      utmSource: data.utmSource,
      utmMedium: data.utmMedium,
      utmCampaign: data.utmCampaign,
      utmContent: data.utmContent,
      utmTerm: data.utmTerm,
      landingPage: data.landingPage || window.location.pathname,
      queryParams:
        data.queryParams || Object.fromEntries(new URLSearchParams(window.location.search))
    })
  } catch (error) {
    // Fire-and-forget — silently fail
    logger.debug('Campaign hit recording failed:', error.message)
  }
}

// ============================================================================
// ADMIN ENDPOINTS
// ============================================================================

/**
 * List campaigns (admin)
 */
export const listCampaigns = async ({ page = 1, limit = 25, search = '', status, type } = {}) => {
  const params = { page, limit }
  if (search) params.search = search
  if (status) params.status = status
  if (type) params.type = type
  const response = await api.get('/campaigns/admin', { params })
  return response.data
}

/**
 * Create a campaign (admin)
 */
export const createCampaign = async (data) => {
  const response = await api.post('/campaigns/admin', data)
  return response.data
}

/**
 * Get campaign detail (admin)
 */
export const getCampaign = async (id) => {
  const response = await api.get(`/campaigns/admin/${id}`)
  return response.data
}

/**
 * Update a campaign (admin)
 */
export const updateCampaign = async (id, data) => {
  const response = await api.put(`/campaigns/admin/${id}`, data)
  return response.data
}

/**
 * Archive a campaign (admin)
 */
export const archiveCampaign = async (id) => {
  const response = await api.post(`/campaigns/admin/${id}/archive`)
  return response.data
}

/**
 * Get dashboard-level analytics (admin)
 */
export const getDashboardAnalytics = async ({ startDate, endDate } = {}) => {
  const params = {}
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  const response = await api.get('/campaigns/admin/analytics', { params })
  return response.data
}

/**
 * Get per-campaign analytics (admin)
 */
export const getCampaignAnalytics = async (id, { startDate, endDate } = {}) => {
  const params = {}
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  const response = await api.get(`/campaigns/admin/${id}/analytics`, { params })
  return response.data
}

/**
 * Get paginated hits for a campaign (admin)
 */
export const getCampaignHits = async (id, { page = 1, limit = 50 } = {}) => {
  const response = await api.get(`/campaigns/admin/${id}/hits`, { params: { page, limit } })
  return response.data
}

/**
 * Get paginated conversions for a campaign (admin)
 */
export const getCampaignConversions = async (id, { page = 1, limit = 50 } = {}) => {
  const response = await api.get(`/campaigns/admin/${id}/conversions`, { params: { page, limit } })
  return response.data
}

/**
 * Export campaigns as CSV (admin)
 */
export const exportCampaignsCSV = async ({ status, type } = {}) => {
  const params = {}
  if (status) params.status = status
  if (type) params.type = type
  const response = await api.get('/campaigns/admin/export', { params, responseType: 'blob' })
  return response.data
}

export default {
  getVisitorId,
  resolveTrackingCode,
  recordCampaignHit,
  listCampaigns,
  createCampaign,
  getCampaign,
  updateCampaign,
  archiveCampaign,
  getDashboardAnalytics,
  getCampaignAnalytics,
  getCampaignHits,
  getCampaignConversions,
  exportCampaignsCSV
}
